import { useRef, useState } from 'react';
import styled from 'styled-components';
import { mediaMSmMax } from 'helpers/breakpoints';
import FadeInSection from 'components/homepage/fadeInSection';
import { useAnimationHook } from 'components/homepage/animationUtils';
import NextImageWithShimmer from 'components/image/NextImageWithShimmer';
import { useBreakpoint } from 'components/BreakpointProvider';
import Link from 'next/link';
import boatsetterPaths from 'utils/boatsetterPaths';
import BuoyLogo from 'components/dashboard/renterInsurance/components/BuoyLogo';

const BoatSharing = () => {
  const [isVisible, setVisible] = useState(false);
  const [readMore, setReadMore] = useState(false);
  const domRef = useRef();
  const breakpoints = useBreakpoint();
  const isMobile = breakpoints.xs || breakpoints.sm;

  useAnimationHook(domRef, setVisible);

  const handleReadMore = () => {
    setReadMore(true);
  };

  return (
    <MainContainer>
      <div className="top">
        <h2>What is boat sharing?</h2>
        <FadeInSection isSlow isSmallerMargin isVisibleProp={isVisible}>
          <div className="imgWrap">
            <NextImageWithShimmer
              src="spa/about/boat-sharing.png"
              alt="Share a boat"
              height={isMobile ? 180 : 380}
              width={isMobile ? 289 : 852}
              style={{
                maxWidth: '100%',
                height: 'auto',
              }}
            />
          </div>
        </FadeInSection>
        <FadeInSection isSlow isRight isVisibleProp={isVisible} isSmallerMargin>
          <div className="imgSmall">
            <NextImageWithShimmer
              src="spa/about/boat-sharing-small.png"
              alt="Share a boat"
              height={isMobile ? 116 : 240}
              width={isMobile ? 164 : 339}
              style={{
                maxWidth: '100%',
                height: 'auto',
              }}
            />
          </div>
        </FadeInSection>
      </div>
      <div className={`bottom ${!readMore ? 'read-more-style' : ''}`}>
        <div className="bottom-body-top">
          <p>
            Here at Boatsetter, we’re proud to offer the{' '}
            <span className="bold-black">largest and most diverse fleet of peer-to-peer boat rentals</span> across the
            United States and abroad. If you’re unfamiliar with what boat sharing is, that’s okay! A trailblazer in the
            global sharing economy, our always evolving online marketplace and booking services help connect current
            boat owners with prospective boat renters.
            <br />
            <br />
            <span className="bold-black">
              Our goal is to deliver easy access to boating for everyone, everywhere, while also enabling boat owners to
              earn money to
            </span>{' '}
            <Link href={boatsetterPaths.whyList} passHref className="bold-blue">
              offset the costs of boat ownership.
            </Link>{' '}
            Boatsetter empowers true entrepreneurship on the water. Thanks to our exclusive partnership with Buoy, we
            are the only insured peer-to-peer boat sharing marketplace in the U.S.
          </p>
        </div>
        {!readMore && (
          <div className="read-more" onClick={handleReadMore}>
            <p>Read more</p>
            <svg className="more" xmlns="http://www.w3.org/2000/svg" height={25} viewBox="0 0 1008 1008">
              <path d="M875.4 349.4c0 6.6-4 13.2-7.9 17.2L521.2 712.8c-4 4-11.9 7.9-17.2 7.9-6.6 0-13.2-4-17.2-7.9L140.5 366.6c-4-4-7.9-10.6-7.9-17.2 0-5.3 4-13.2 7.9-17.2l37-37c4-4 10.6-7.9 17.2-7.9 5.3 0 13.2 4 17.2 7.9L504 587.3l292.1-292.1c4-4 11.9-7.9 17.2-7.9 6.6 0 13.2 4 17.2 7.9l37 37c3.9 4 7.9 11.9 7.9 17.2z" />
            </svg>
          </div>
        )}
        {readMore && (
          <div className="mobile">
            <div className="bottom-body-mid">
              <div className="imgWrap-mob">
                <NextImageWithShimmer
                  src="spa/about/boat-sharing-2.png"
                  alt="Share a boat"
                  fill
                  sizes="100vw"
                  style={{
                    objectFit: 'cover',
                  }}
                />
              </div>
              <div className="right">
                <p>
                  That means that all boat owners participating on the Boatsetter platform can safely and confidently
                  rent out their boats with the peace of mind that both owner and renter are{' '}
                  <a href="https://www.buoy.rent/national-coverage" className="bold-blue">
                    protected by our policy.
                  </a>
                  <br />
                  <br />
                  Through the Boatsetter website and boat sharing app,{' '}
                  <span className="bold-black">
                    renters everywhere can choose from a totally unique list of vessels that suit their exact needs,
                    location, and lifestyle.
                  </span>{' '}
                  Communication between owners and renters is easy and secure, so you can chat through the details and
                  plan your day in an instant. Opt to have a licensed captain onboard for stress-free cruising, or take
                  the helm yourself and set your course towards adventure.
                </p>
                <div className="logo-row">
                  <BuoyLogo />
                </div>
              </div>
            </div>
            <div className="bottom-body-down">
              <p>
                Whether it&apos;s a center console fishing machine to hook your trophy catch, a powerful wake boat
                designed to produce waves you can ride forever, or a party pontoon filled with music and good times,
                Boatsetter allows you to find the perfect boat for your next on-water experience—no matter where the
                seas take you.
              </p>
            </div>
          </div>
        )}
        <div className="desktop">
          <div className="bottom-body-mid">
            <div className="imgWrap">
              <NextImageWithShimmer
                src="spa/about/boat-sharing-2.png"
                alt="Share a boat"
                height={329}
                width={614}
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                }}
              />
            </div>
            <div className="right">
              <p>
                That means that all boat owners participating on the Boatsetter platform can safely and confidently rent
                out their boats with the peace of mind that both owner and renter are{' '}
                <a href="https://www.buoy.rent/national-coverage" className="bold-blue">
                  protected by our policy.
                </a>
                <br />
                <br />
                Through the Boatsetter website and boat sharing app,{' '}
                <span className="bold-black">
                  renters everywhere can choose from a totally unique list of vessels that suit their exact needs,
                  location, and lifestyle.
                </span>{' '}
                Communication between owners and renters is easy and secure, so you can chat through the details and
                plan your day in an instant. Opt to have a licensed captain onboard for stress-free cruising, or take
                the helm yourself and set your course towards adventure.
              </p>
              <div className="logo-row">
                <BuoyLogo />
              </div>
            </div>
          </div>
          <div className="bottom-body-down">
            <p>
              Whether it&apos;s a center console fishing machine to hook your trophy catch, a powerful wake boat
              designed to produce waves you can ride forever, or a party pontoon filled with music and good times,
              Boatsetter allows you to find the perfect boat for your next on-water experience—no matter where the seas
              take you.
            </p>
          </div>
        </div>
      </div>
    </MainContainer>
  );
};

export default BoatSharing;

const MainContainer = styled.div`
  max-width: 1100px;
  margin: 0 auto;

  ${mediaMSmMax} {
    margin-top: 65px;
  }

  .top {
    position: relative;
    z-index: 3;
    padding-top: 73px;

    ${mediaMSmMax} {
      padding: 30px 22px 0 22px;
    }

    h2 {
      font-style: normal;
      font-weight: bold;
      font-size: 32px;
      line-height: 40px;
      color: #17233c;
      margin: 0 0 49px 0;

      ${mediaMSmMax} {
        font-size: 24px;
        line-height: 30px;
        margin: 0 0 18px 0;
      }
    }

    .imgWrap {
      width: 852px;
      height: 380px;
      border-radius: 20px;
      overflow: hidden;

      ${mediaMSmMax} {
        width: 289px;
        height: 180px;
        border-radius: 16px;
      }

      img {
        ${mediaMSmMax} {
          object-fit: cover;
          border-radius: 16px;
        }
      }
    }

    .imgSmall {
      position: absolute;
      bottom: 60px;
      right: 0;

      ${mediaMSmMax} {
        right: 22px;
        bottom: -32px;
      }
    }
  }

  .bottom {
    background: rgba(238, 243, 251, 0.57);
    width: 100vw;
    margin-left: calc(-50vw + 50%);
    margin-top: -12em;
    padding-bottom: 60px;
    margin-bottom: 90px;
    position: relative;
    z-index: 0;

    ${mediaMSmMax} {
      margin-bottom: 45px;
      padding-bottom: 40px;
    }

    &.read-more-style {
      ${mediaMSmMax} {
        position: relative;
        padding-bottom: 0;
        z-index: 1;
      }
    }

    .mobile {
      display: none;

      ${mediaMSmMax} {
        display: block;
      }
    }

    .desktop {
      ${mediaMSmMax} {
        display: none;
      }
    }

    .read-more {
      display: none;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      position: absolute;
      z-index: 2;
      bottom: 0;
      width: 100%;
      height: 130px;
      background: linear-gradient(0deg, #f6f8fd 55%, rgba(246, 248, 253, 0) 100%);

      ${mediaMSmMax} {
        display: flex;
      }

      p {
        font-style: normal;
        font-weight: 500;
        font-size: 16px;
        line-height: 24px;
        text-align: center;
        color: #0751c2;
        margin-bottom: 0px;
        margin-top: 40px;
      }

      .more {
        fill: #0751c2;
      }
    }

    .bold-black {
      font-weight: 600;
      color: #17233c;
    }

    .bold-blue {
      font-weight: 600;
      color: #0751c2;
      text-decoration: none;
    }

    ${mediaMSmMax} {
      margin-top: -6em;
    }

    &::before,
    &::after {
      border-bottom: 5px solid #fff;
    }

    .bottom-body-top {
      max-width: 1100px;
      width: 100%;
      margin: 0 auto;
      padding-top: 14em;
      display: flex;
      justify-content: flex-end;

      p {
        width: 850px;
        font-size: 16px;
        line-height: 140%;
        letter-spacing: -0.2px;
        color: #17233c;
      }

      ${mediaMSmMax} {
        padding-left: 22px;
        padding-right: 22px;
        padding-top: 9em;
      }
    }

    .bottom-body-mid {
      max-width: 1100px;
      width: 100%;
      margin: 0 auto;
      display: flex;
      justify-content: space-between;
      margin-top: 36px;

      ${mediaMSmMax} {
        flex-direction: column;
        max-width: 100%;
        padding: 0 22px;
        margin-top: 30px;
      }

      .imgWrap {
        img {
          border-radius: 20px;
        }
      }

      .imgWrap-mob {
        width: 100%;
        height: auto;
        min-height: 179px;
        overflow: hidden;
        position: relative;
        margin-bottom: 30px;

        img {
          border-radius: 10px;
        }
      }

      .right {
        p {
          width: 410px;
          margin-left: 50px;
          font-size: 16px;
          line-height: 140%;
          color: #17233c;

          ${mediaMSmMax} {
            margin-left: 0;
            width: 100%;
          }
        }

        .logo-row {
          display: flex;
          margin-left: 50px;
          align-items: center;

          ${mediaMSmMax} {
            margin-left: 0;
          }

          img {
            width: 122px;
            height: 20px;
            margin-right: 3px;

            ${mediaMSmMax} {
              width: 107px;
              height: 18px;
            }

            &.mid {
              width: 33px;
              height: 36px;

              ${mediaMSmMax} {
                width: 29px;
                height: 32px;
              }
            }
          }
        }
      }
    }

    .bottom-body-down {
      max-width: 1100px;
      width: 100%;
      margin: 0 auto;
      display: flex;
      justify-content: flex-end;
      margin-top: 36px;

      ${mediaMSmMax} {
        padding: 0 22px;
        margin-top: 16px;
      }

      p {
        width: 850px;
        font-size: 16px;
        line-height: 140%;
        color: #17233c;
      }
    }
  }
`;
